<template>
  <AppModal
    ref="modalRef"
    model-value
    body-class="col full-width border-bottom bg-grey-1"
    footer-class="q-pa-lg flex items-center border-top full-width"
    position="right"
    show-close-btn
    no-esc-dismiss
    no-route-dismiss
    :title="$t('text.createNewContent')"
    content-style="width: 600px;"
    @hide="$emit('hide')"
  >
    <q-form
      ref="formRef"
      class="flex column full-height"
    >
      <div class="bg-white q-pa-lg q-mb-md">
        <div
          v-t="'label.targetKeywords'"
          class="text-weight-medium text-grey q-mb-sm"
        />
        <AppSelectWithSearch
          v-model="targetKeyword"
          :options="keywordOptions"
          :rules="[
            rules.REQUIRED,
            (v) => rules.MAX_LENGTH(v, $t('label.targetKeywords'), TARGET_KEYWORD_MAX_LENGTH),
          ]"
          menu-style="width:552px;margin-top:-20px !important;"
          @select="selectKeyword"
        />
      </div>
      <div class="q-pa-lg">
        <div class="bg-white border-radius-12 q-pa-md q-mb-md q-pt-lg">
          <AppInput
            v-model="form.headersGenerationFields.title"
            :rules="[rules.REQUIRED]"
            :loading="isLoading"
            :disable="isLoading"
            :label="$t('label.titleH1')"
            :placeholder="$t('text.postTitlePlaceholder')"
            class="q-mb-sm"
            outlined
          />
        </div>
      </div>
      <div class="description flex justify-center items-center col column text-center">
        <div
          class="fs-16 q-mb-md"
          v-html="$t('text.youCreatingEmptyPost')"
        />
        <div
          class="fs-12 text-grey"
          v-html="$t('text.ifYouNeedHelpFromAI')"
        />
      </div>
    </q-form>
    <template #footer>
      <AppBtn
        :loading="isLoading"
        :label="$t('button.goToEditor')"
        class="bg-gradient flex-1 q-ml-md text-white flex border-radius-10"
        flat
        @click="goToEditor"
      >
        <q-icon
          name="far fa-arrow-right"
          class="q-ml-sm"
          size="16px"
        />
      </AppBtn>
    </template>
  </AppModal>
</template>

<script setup>
import { ref } from 'vue';

import AppModal from 'components/AppModal.vue';
import AppInput from 'components/AppInput.vue';
import AppBtn from 'components/AppBtn.vue';
import { storeToRefs } from 'pinia';
import { useBlogStore } from 'src/stores/blog';
import { useKeywordsStore } from 'stores/keywords';
import { useAccountStore } from 'stores/account';
import { ROUTES } from 'src/enums';
import { useRouter } from 'vue-router';
import rules from 'src/helpers/rules';
import handleError from 'src/helpers/handleError';
import AppSelectWithSearch from 'components/AppSelectWithSearch.vue';

const blogStore = useBlogStore();
const keywordsStore = useKeywordsStore();
const accountStore = useAccountStore();
const { post, blog } = storeToRefs(blogStore);
const { favorites } = storeToRefs(keywordsStore);

const TARGET_KEYWORD_MAX_LENGTH = 100;

const props = defineProps({
  keyword: {
    type: Object,
    default: null,
  },
});
const keywordOptions = ref([]);

const router = useRouter();

const modalRef = ref(null);
const formRef = ref(null);
const isLoading = ref(false);
const targetKeyword = ref(null);

const form = ref({
  targetKeywordGuid: null,
  headersGenerationFields: {
    title: null,
  },
  metaFields: {},
  contentType: 'manual',
});

defineEmits(['hide']);

function selectKeyword(val) {
  const keyword = favorites.value.find((item) => item.title === val);
  form.value.targetKeywordGuid = keyword?.guid || null;
}

async function goToEditor() {
  const isValid = await formRef.value.validate();
  if (isValid) {
    try {
      isLoading.value = true;
      const keyword = favorites.value.find((item) => item.title === targetKeyword.value);
      form.value.targetKeywordGuid = keyword?.guid || null;
      if (!form.value.targetKeywordGuid) {
        const { data } = await keywordsStore.createKeyword({
          title: targetKeyword.value,
          volume: null,
          difficulty: null,
        });
        form.value.targetKeywordGuid = data.guid;
      }
      await blogStore.createPost(form.value);
      await accountStore.getAccount();
      router.push({
        name: ROUTES.POSTS.EDIT,
        params: { postGuid: post.value.guid, blogGuid: blog.value.guid },
      });
    } finally {
      isLoading.value = false;
    }
  }
}

(async function () {
  try {
    isLoading.value = true;
    await keywordsStore.getFavoritesKeywords();
    if (props.keyword?.guid) {
      form.value.targetKeywordGuid = props.keyword.guid;
    }
    targetKeyword.value = props.keyword?.title || null;
    keywordOptions.value = favorites.value.map((item) => item.title);
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
}());
</script>

<style lang="scss" scoped>
:deep(.q-chip) {
  background: transparent;

  .q-icon {
    display: none;
  }
}

:deep(.q-field__append) {
  .text-negative {
    display: none;
  }
}
</style>
